import { NAVIGATION_SNAPSHOT_MAX_AGE_MS } from "./navigation-types";
import {
  movementRegionBounds,
  pilotMovementObstacles,
} from "@frontier/simulation/movement";
import {
  NavigationPathSearch,
  navigationSegmentClear,
} from "./navigation-path";
import type { NavigationGeometry } from "./navigation-path";
import type { Point } from "./geometry";
import type {
  NavigationCancelReason,
  NavigationContext,
  NavigationState,
  NavigationSteering,
  PointNavigation,
} from "./navigation-types";


const MAX_PLANNING_MS = 4000;
const COLLISION_CHECK_MS = 250;
const STUCK_MS = 2000;
const ARRIVAL_DISTANCE = 8;
const ARRIVAL_DRIFT_DISTANCE = 20;
const ARRIVAL_SETTLE_MS = 500;
const FINAL_APPROACH_SECONDS = 0.4;
const WAYPOINT_DISTANCE = 2;
const INPUT_STEP_SECONDS = 1 / 20;
const MAX_REPLANS = 3;
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

/** Chooses ordinary control intentions; movement and all interactions remain server-owned. */
export class Autopilot implements PointNavigation {
  private current: NavigationState = { status: "idle", reason: null };
  private search: NavigationPathSearch | null = null;
  private geometry: NavigationGeometry | null = null;
  private path: Point[] = [];
  private cursor = 0;
  private destination: Point | null = null;
  private identity = "";
  private region = "";
  private startedAt = 0;
  private searchStepAt = -Infinity;
  private collisionCheckAt = -Infinity;
  private progressAt = 0;
  private progressPosition: Point = { x: 0, y: 0 };
  private replans = 0;
  private maxSpeed = 1;
  private lastReceivedAt = 0;
  private lastAuthoritative: Point = { x: 0, y: 0 };
  private settlingAt: number | null = null;
  private settlingSnapshotAt = 0;
  private arrivalCorrections = 0;

  constructor(
    private readonly context: () => NavigationContext | null,
    private readonly onChange: (state: NavigationState) => void,
  ) {}

  get state(): NavigationState {
    return this.current;
  }

  navigate(point: Point, now = performance.now()): void {
    const context = this.available(now);
    if (!context) return;
    const movement = context.pilot.controls?.movement;
    if (!movement) return;
    const bounds = movementRegionBounds(context.pilot, context.world.boundaryRules ? context.pilot.sectorId : undefined);
    if (
      !Number.isFinite(point.x) ||
      !Number.isFinite(point.y) ||
      (!context.world.boundaryRules && (point.x < bounds.x ||
      point.y < bounds.y ||
      point.x >= bounds.x + bounds.width ||
      point.y >= bounds.y + bounds.height))
    ) {
      this.cancel("outside-region");
      return;
    }
    this.identity = this.pilotIdentity(context);
    this.region = this.regionIdentity(context);
    this.lastReceivedAt = context.receivedAt;
    this.lastAuthoritative = { x: context.pilot.x, y: context.pilot.y };
    this.replans = 0;
    this.arrivalCorrections = 0;
    this.maxSpeed = movement.speed * movement.rules.boostSpeedFactor * movement.boostSpeedFactor;
    this.startPlan(context, point, now);
  }

  cancel(reason: NavigationCancelReason = "cancelled"): void {
    this.search = null;
    this.path = [];
    this.cursor = 0;
    this.destination = null;
    this.settlingAt = null;
    if (this.current.status !== "idle" || this.current.reason !== reason)
      this.publish({ status: "idle", reason });
  }

  steer(now = performance.now()): NavigationSteering | null {
    if (this.current.status === "idle") return null;
    const context = this.available(now);
    if (!context) return null;
    if (this.pilotIdentity(context) !== this.identity) {
      this.cancel("unavailable");
      return null;
    }
    if (this.regionIdentity(context) !== this.region) {
      this.cancel("region-changed");
      return null;
    }
    const freshSnapshot = context.receivedAt !== this.lastReceivedAt;
    if (freshSnapshot) {
      if (distance(context.pilot, this.lastAuthoritative) > 220) {
        this.cancel("unavailable");
        return null;
      }
      this.lastReceivedAt = context.receivedAt;
      this.lastAuthoritative = { x: context.pilot.x, y: context.pilot.y };
      if (distance(context.pilot, this.progressPosition) >= 10) {
        this.progressAt = now;
        this.progressPosition = { x: context.pilot.x, y: context.pilot.y };
        this.replans = 0;
      }
    }
    if (this.current.status === "blocked") return null;
    if (this.current.status === "arrived") {
      if (
        freshSnapshot &&
        this.destination &&
        distance(context.pilot, this.destination) > ARRIVAL_DRIFT_DISTANCE
      )
        return this.correctArrival(context, now);
      return null;
    }
    if (this.search) {
      if (now - this.startedAt > MAX_PLANNING_MS) return this.block();
      if (now - this.searchStepAt < 16) return null;
      this.searchStepAt = now;
      const result = this.search.advanceFrame();
      if (result.status === "searching") return null;
      if (result.status === "blocked") return this.block();
      this.search = null;
      this.destination = { ...result.destination };
      this.path = result.path.map((point) => ({ ...point }));
      this.cursor = 0;
      this.progressAt = now;
      this.progressPosition = { x: context.pilot.x, y: context.pilot.y };
      this.publishRoute();
    }
    const destination = this.destination;
    if (!destination) return null;
    const withinArrival =
      distance(context.position, destination) <= ARRIVAL_DISTANCE &&
      distance(context.pilot, destination) <= ARRIVAL_DISTANCE;
    if (this.settlingAt !== null) {
      // Let already-sent movement drain before deciding whether another correction is needed.
      if (now - this.settlingAt >= ARRIVAL_SETTLE_MS) {
        if (!withinArrival) return this.correctArrival(context, now);
        if (context.receivedAt !== this.settlingSnapshotAt) {
          this.path = [];
          this.settlingAt = null;
          this.publish({ status: "arrived", destination: { ...destination } });
        }
      }
      return { movement: { x: 0, y: 0 }, aim: { ...destination } };
    }
    if (withinArrival) {
      this.settlingAt = now;
      this.settlingSnapshotAt = context.receivedAt;
      return { movement: { x: 0, y: 0 }, aim: { ...destination } };
    }
    let next = this.path[this.cursor];
    while (
      next &&
      this.cursor < this.path.length - 1 &&
      distance(context.position, next) <= WAYPOINT_DISTANCE
    ) {
      this.cursor++;
      next = this.path[this.cursor];
      this.publishRoute();
    }
    if (!next) return this.block();
    if (now - this.collisionCheckAt >= COLLISION_CHECK_MS) {
      this.collisionCheckAt = now;
      this.geometry = this.navigationGeometry(context);
      if (!navigationSegmentClear(context.position, next, this.geometry))
        return this.replan(context, now);
    }
    if (now - this.progressAt >= STUCK_MS) return this.replan(context, now);
    const length = distance(context.position, next);
    if (length < 0.001) return { movement: { x: 0, y: 0 }, aim: { ...next } };
    const responseSeconds =
      this.cursor === this.path.length - 1
        ? FINAL_APPROACH_SECONDS
        : INPUT_STEP_SECONDS;
    const scale = Math.min(1, length / (this.maxSpeed * responseSeconds));
    return {
      movement: {
        x: ((next.x - context.position.x) / length) * scale,
        y: ((next.y - context.position.y) / length) * scale,
      },
      aim: { ...next },
    };
  }

  private available(now: number): NavigationContext | null {
    const context = this.context();
    if (
      !context ||
      !context.runId ||
      !context.pilot.controls ||
      context.pilot.dead ||
      context.pilot.docked
    ) {
      this.cancel("unavailable");
      return null;
    }
    if (now - context.receivedAt > NAVIGATION_SNAPSHOT_MAX_AGE_MS) {
      this.cancel("stale");
      return null;
    }
    return context;
  }

  private startPlan(
    context: NavigationContext,
    destination: Point,
    now: number,
  ): void {
    this.destination = { ...destination };
    this.path = [];
    this.cursor = 0;
    this.geometry = this.navigationGeometry(context);
    this.search = new NavigationPathSearch(
      context.position,
      destination,
      this.geometry,
    );
    this.startedAt = now;
    this.searchStepAt = -Infinity;
    this.collisionCheckAt = -Infinity;
    this.progressAt = now;
    this.progressPosition = { x: context.pilot.x, y: context.pilot.y };
    this.settlingAt = null;
    this.publish({ status: "planning", destination: { ...destination } });
  }

  private correctArrival(context: NavigationContext, now: number): null {
    if (!this.destination || this.arrivalCorrections >= MAX_REPLANS)
      return this.block();
    this.arrivalCorrections++;
    this.startPlan(context, this.destination, now);
    return null;
  }

  private replan(context: NavigationContext, now: number): null {
    if (!this.destination || this.replans >= MAX_REPLANS) return this.block();
    this.replans++;
    this.startPlan(context, this.destination, now);
    return null;
  }

  private block(): null {
    const destination = this.destination;
    this.search = null;
    this.path = [];
    this.settlingAt = null;
    if (destination)
      this.publish({ status: "blocked", destination: { ...destination } });
    return null;
  }

  private navigationGeometry(context: NavigationContext): NavigationGeometry {
    const sectorId = context.world.boundaryRules ? context.pilot.sectorId : undefined;
    const bounds = { ...movementRegionBounds(context.pilot, sectorId) };
    if (context.world.boundaryRules && this.destination) {
      const margin = context.pilot.radius * 2;
      const right = Math.max(bounds.x + bounds.width, context.position.x + margin, this.destination.x + margin);
      const bottom = Math.max(bounds.y + bounds.height, context.position.y + margin, this.destination.y + margin);
      bounds.x = Math.min(bounds.x, context.position.x - margin, this.destination.x - margin);
      bounds.y = Math.min(bounds.y, context.position.y - margin, this.destination.y - margin);
      bounds.width = right - bounds.x;
      bounds.height = bottom - bounds.y;
    }
    return {
      bounds,
      radius: context.pilot.radius,
      obstacles: pilotMovementObstacles(
        context.world.obstacles,
        [...context.world.npcs, ...context.world.players],
        context.intro,
        context.pilot.id,
        sectorId,
      ),
    };
  }

  private pilotIdentity(context: NavigationContext): string {
    return `${context.pilot.id}:${context.runId}:${context.pilot.hullId}:${context.pilot.radius}:${JSON.stringify(context.pilot.controls)}`;
  }

  private regionIdentity(context: NavigationContext): string {
    const bounds = movementRegionBounds(context.pilot, context.world.boundaryRules ? context.pilot.sectorId : undefined);
    return `${bounds.x}:${bounds.y}:${bounds.width}:${bounds.height}`;
  }

  private publishRoute(): void {
    if (this.destination)
      this.publish({
        status: "flying",
        destination: { ...this.destination },
        path: this.path.slice(this.cursor).map((point) => ({ ...point })),
      });
  }

  private publish(state: NavigationState): void {
    this.current = state;
    this.onChange(state);
  }
}
